import useAllSuppliers from '@/api/accounts-payable/useAllSuppliersQuery';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { useState, useMemo } from 'react';
import { useSupplier } from '@/api/rpc/hooks/accounts-payable/useSupplier';
import { useGT } from 'gt-react';

/**
 * ENHANCEMENT TODO: Show default currency next to supplier name
 *
 * Currently, suppliers don't include company information in their DTO.
 * To show currency in this autocomplete, we would need to either:
 * 1. Update the suppliers API to join with companies and include defaultCurrency
 * 2. Fetch companies separately and map them (performance concern for large lists)
 * 3. Add a lazy-loaded currency badge that fetches on hover/display
 *
 * For now, currency can be viewed/edited in:
 * - Company detail page (edit form)
 * - Automatically populated in CSV workflows when supplier is selected
 *
 * @see packages/shared-types/src/currency.ts for currency handling
 * @see frontend/src/components/company/CompanyEditForm.tsx for currency edit UI
 */

export function SupplierAutocomplete({
  supplierId,
  onSelect,
  className,
  module,
}: {
  supplierId: string | undefined;
  onSelect: (supplierId: string | undefined) => void;
  className?: string;
  module?: string;
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Only fetch all suppliers when the dropdown is opened or user is searching
  const shouldFetchAll = isOpen || search.length > 0;
  const { data: allSuppliers } = useAllSuppliers(
    {
      search: search || undefined,
      module,
    },
    shouldFetchAll, // enabled parameter
  );

  // Fetch the selected supplier to ensure it's always available for display
  const { data: selectedSupplier } = useSupplier(supplierId);

  // Merge the selected supplier into the options if it's not already there
  const options = useMemo(() => {
    // If we haven't fetched all suppliers yet, just show the selected one
    if (!allSuppliers) {
      return selectedSupplier ? [selectedSupplier] : [];
    }

    // If there's a selected supplier and it's not in the list, add it
    if (
      selectedSupplier &&
      !allSuppliers.some((s) => s.id === selectedSupplier.id)
    ) {
      return [selectedSupplier, ...allSuppliers];
    }

    return allSuppliers;
  }, [allSuppliers, selectedSupplier]);

  return (
    <Autocomplete
      value={supplierId}
      getValue={(supplier) => supplier.id}
      getLabel={(supplier) => supplier.name}
      options={options}
      searchValue={search}
      onSearchValueChange={setSearch}
      onValueChange={onSelect}
      open={isOpen}
      onOpenChange={setIsOpen}
      placeholder={gt('Search suppliers', {
        context: 'accounts payable supplier autocomplete',
      })}
      multiSelect={false}
      allowClear
      className={className}
    />
  );
}
